// src/scenes/ProfileScene.js - CLEAN MODULAR IMPLEMENTATION
import firebase, { auth, db } from "../firebaseConfig.js";
import {
  getPlayerBaseline,
  getPlayerLevelBests,
  calculatePlayerImprovement,
  getRecentSessionStats,
  getRecentLevelPerformances,
  handleCalibrationReset
} from "../services/analyticsService.js";
import { updateDisplayName } from "../services/profileService.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import BaseMenuScene from "./BaseMenuScene.js";
import { populateAnalyticsOverlay } from "../ui/analyticsUI.js";
import UIController from "../ui/uiController.js";
import sceneManager from "../utils/SceneManager.js";
import { getCompanyEmailStatus } from "../services/firmAuthService.js";
import ProfileDialogManager from "./ProfileDialogManager.js";
import ProfileEmailManager from "./ProfileEmailManager.js";
import ProfileSceneUI from "./ProfileSceneUI.js";

export default class ProfileScene extends BaseMenuScene {
  constructor() {
    super({ key: "ProfileScene" });
    this.dataLoaded = false;
    // Initialize modular managers
    this.dialogManager = new ProfileDialogManager(this);
    this.emailManager = new ProfileEmailManager(this);
    this.uiManager = new ProfileSceneUI(this);
  }

  preload() {
    // Load Chart.js for Rex UI Chart component (REQUIRED for radar charts)
    this.load.script('chartjs', 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.8.0/chart.min.js');
  }

  init(data) {
    super.init(data);
    this.activeTab = data?.activeTab || "profile";
    this.tabContent = {};
    this.uiElements = {};
    
    if (data && data.loadedData) {
        this.dataLoaded = true;
        Object.assign(this, data.loadedData);
    } else {
        this.dataLoaded = false;
    }
  }

  create() {
    super.create();
    
    // Matrix cyber professional background
    this.createMatrixExecutiveBackground();

    if (!auth.currentUser) {
      sceneManager.switchToScene("MainMenuScene");
      return;
    }

    if (this.dataLoaded) {
      this.buildFullUI();
    } else {
      // Fallback loading state
      this.uiElements.loadingText = this.add.text(
        this.cameras.main.width / 2, 
        this.cameras.main.height / 2, 
        "Loading profile data...", 
        { fontFamily: "monospace", fontSize: "24px", fill: "#00FF00" }
      ).setOrigin(0.5);
      this.loadAndRestart();
    }
  }

  createMatrixExecutiveBackground() {
    // MATRIX CYBER PROFESSIONAL background - black with subtle green grid
    this.cameras.main.setBackgroundColor("#000000");
    
    // Matrix grid pattern for cyber aesthetic
    const graphics = this.add.graphics();
    graphics.setDepth(-100); // Behind everything
    
    // MATRIX grid - subtle but recognizable
    graphics.lineStyle(1, 0x00FF00, 0.08); // Very subtle achievement green
    const gridSize = 50;
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Vertical matrix lines
    for (let x = 0; x <= width; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }
    
    // Horizontal matrix lines  
    for (let y = 0; y <= height; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }
    
    graphics.strokePath();
  }

  async loadAndRestart() {
    try {
      const loadedData = await this.loadAllData();
      
      if (this.scene.isActive(this.scene.key)) {
        this.scene.restart({ loadedData: loadedData, activeTab: this.activeTab });
      }
    } catch (error) {
      console.error("Error during profile data loading:", error);
      this.showMessage("Error loading profile data. Please try again.", 3000, true);
      const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
      sceneManager.switchToScene(useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene");
    }
  }

  buildFullUI() {
    this.createProfessionalRexUI();
    this.handleResize();
  }

  createProfessionalRexUI() {
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    
    // Check if Rex UI is available
    if (!this.rexUI) {
      console.error("Rex UI not available - falling back to simple implementation");
      this.createFallbackUI();
      return;
    }

    console.log("✅ Building PROFESSIONAL Rex UI Profile with modular managers...");
    
    // Create professional interface using UI manager
    this.createReliableProfessionalUI(centerX, centerY);
    
    console.log("✅ Professional Profile created successfully!");
  }

  createReliableProfessionalUI(centerX, centerY) {
    // Use the existing TabPages implementation with corrected API
    this.createProfessionalTabPages(centerX, centerY);
  }

  // RESTORED: Original working createProfessionalTabPages method
  createProfessionalTabPages(centerX, centerY) {
    // Use the modular UI manager for all tab/UI creation
    this.uiManager.createProfessionalTabPages(centerX, centerY);
  }

  // Data loading methods
  async loadAllData() {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated.");

    const loadedData = {
        profileData: {},
        pptScores: [], shortcutScores: [], classifyTechScores: [],
        pptGamesPlayed: 0, shortcutGamesPlayed: 0, classifyTechGamesPlayed: 0,
        pptBestScore: 0, shortcutBestScore: 0, classifyTechBestScore: 0,
        pptHighestLevel: 1, shortcutHighestLevel: 1,
        pptPlayerRank: "Not ranked", shortcutPlayerRank: "Not ranked", classifyTechPlayerRank: "Not ranked"
    };

    const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
    loadedData.profileData = profileDoc.exists ? profileDoc.data() : { 
      displayName: user.displayName || user.email, 
      email: user.email, 
      createdAt: firebase.firestore.Timestamp.now() 
    };
    
    // Load company email status using EmailManager
    loadedData.companyEmailStatus = await this.emailManager.loadCompanyEmailStatus(user.uid);

    [loadedData.baselinePPT, loadedData.levelBestsPPT, loadedData.improvementPPT, loadedData.recentSessionStatsPPT, loadedData.recentPerformancesPPT] = await Promise.all([
        getPlayerBaseline(user.uid).catch(e => null),
        getPlayerLevelBests(user.uid, "PPTHero").catch(e => []),
        calculatePlayerImprovement(user.uid, "PPTHero").catch(e => null),
        getRecentSessionStats(user.uid, "PPTHero").catch(e => null),
        getRecentLevelPerformances(user.uid, "PPTHero").catch(e => [])
    ]);

    if (loadedData.levelBestsPPT?.length > 0) {
        const highestLevel = Math.max(...loadedData.levelBestsPPT.filter(l => l && typeof l.levelNumber === 'number').map(l => l.levelNumber));
        loadedData.pptHighestLevel = highestLevel > 0 ? highestLevel : 1;
    }

    await this.loadScores(user.uid, loadedData);
    this.calculateRanks(user.uid, loadedData);
    
    return loadedData;
  }

  async loadScores(userId, data) {
    try {
      const gameTypes = ["PPTHero", "Shortcut", "ClassifyTech"];
      const scorePromises = gameTypes.map(gameType => {
        let queryGameTypes = (gameType === "Shortcut") ? ["Shortcut", "slide wars", "Slide Wars", "MultiplayerShortcut"] : [gameType];
        return db.collection("scores").where("game", "in", queryGameTypes).orderBy("score", "desc").limit(10).get();
      });
      const userScorePromises = gameTypes.map(gameType => {
         let queryGameTypes = (gameType === "Shortcut") ? ["Shortcut", "slide wars", "Slide Wars", "MultiplayerShortcut"] : [gameType];
         return db.collection("scores").where("userUID", "==", userId).where("game", "in", queryGameTypes).get();
       });

      const [pptSnap, shortcutSnap, classifySnap, userPptSnap, userScSnap, userClassifySnap] = await Promise.all([...scorePromises, ...userScorePromises]);

      pptSnap.forEach(d => data.pptScores.push(d.data()));
      shortcutSnap.forEach(d => data.shortcutScores.push(d.data()));
      classifySnap.forEach(d => data.classifyTechScores.push(d.data()));

      data.pptGamesPlayed = userPptSnap.size;
      userPptSnap.forEach(doc => { if (doc.data().score > data.pptBestScore) data.pptBestScore = doc.data().score; });

      data.shortcutGamesPlayed = userScSnap.size;
      userScSnap.forEach(doc => { if (doc.data().score > data.shortcutBestScore) data.shortcutBestScore = doc.data().score; });

      data.classifyTechGamesPlayed = userClassifySnap.size;
      userClassifySnap.forEach(doc => { if (doc.data().score > data.classifyTechBestScore) data.classifyTechBestScore = doc.data().score; });
    } catch (e) {
      console.error("Error loading scores:", e);
    }
  }

  calculateRanks(userId, data) {
    const calculateRankForGame = (scoresArray, bestScore) => {
        if (scoresArray.length === 0 && bestScore > 0) return "Not ranked";
        if (scoresArray.length === 0) return "N/A";
        const userIndex = scoresArray.findIndex(s => s.userUID === userId);
        return userIndex !== -1 ? `#${userIndex + 1}` : "Not ranked";
    };
    data.pptPlayerRank = calculateRankForGame(data.pptScores, data.pptBestScore);
    data.shortcutPlayerRank = calculateRankForGame(data.shortcutScores, data.shortcutBestScore);
    data.classifyTechPlayerRank = calculateRankForGame(data.classifyTechScores, data.classifyTechBestScore);
  }

  // Helper utility methods
  getInitials(name) {
    if (!name || name === "N/A") return "??";
    return name.split(' ').map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2);
  }

  // Matrix Achievement Button - required by ProfileSceneUI
  createMatrixAchievementButton(title, description, callback, color) {
    // Convert hex color to proper string format for BBCode
    const hexColor = `#${color.toString(16).padStart(6, '0').toUpperCase()}`;
    
    const button = this.rexUI.add.titleLabel({
      width: 520,
      height: 75,
      
      // Matrix cyber professional styling - black background with achievement accents
      background: this.rexUI.add.roundRectangle(0, 0, 2, 2, 6, 0x111111, color, 3), // Stronger border
      
      // Achievement-focused typography - matrix monospace
      title: this.rexUI.add.BBCodeText(0, 0, `[b][color=${hexColor}]${title}[/color][/b]`, {
        fontSize: '16px',
        fontFamily: 'Consolas, monospace',
        fontWeight: 'bold',
        resolution: 3
      }),
      
      // Matrix description styling
      text: this.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${description}[/color]`, {
        fontSize: '12px',
        fontFamily: 'Consolas, monospace',
        resolution: 2
      }),
      
      align: { title: 'left', text: 'left' },
      space: { left: 25, right: 25, top: 15, bottom: 15, title: 5 }
    });

    // Matrix hover effects - cyber professional interaction
    button.setInteractive({ useHandCursor: true })
      .on('pointerdown', callback)
      .on('pointerover', function() {
        // Matrix glow effect for achievements - lighter background on hover
        this.getElement('background').setFillStyle(0x333333);
        this.getElement('background').setStrokeStyle(4, color); // Stronger border on hover
        this.setScale(1.02);
      })
      .on('pointerout', function() {
        // Reset to matrix state
        this.getElement('background').setFillStyle(0x111111);
        this.getElement('background').setStrokeStyle(3, color); // Reset border
        this.setScale(1.0);
      });

    return button;
  }

  // Button callback methods
  async changeDisplayName() {
    console.log('🎯 ProfileScene: changeDisplayName called');
    console.log('🎯 DialogManager available:', !!this.dialogManager);
    
    try {
      // Use the new DOM dialog instead of prompt
      if (this.dialogManager && typeof this.dialogManager.showIdentityMatrixUpdateDialog === 'function') {
        this.dialogManager.showIdentityMatrixUpdateDialog();
      } else {
        console.error('🎯 DialogManager or method not available, using fallback');
        // Fallback to direct ProfileSceneDOM method
        if (window.ProfileSceneDOM && window.ProfileSceneDOM.showIdentityUpdateDialog) {
          window.ProfileSceneDOM.showIdentityUpdateDialog();
        } else {
          this.showMessage("Identity update feature temporarily unavailable", 2000, true);
        }
      }
    } catch (error) {
      console.error('🎯 Error opening identity dialog:', error);
      this.showMessage("Error opening identity dialog", 2000, true);
    }
  }

  refreshData() {
    this.showMessage("Refreshing...", 1000, false);
    this.scene.restart({ activeTab: this.activeTab });
  }

  async showAnalytics() {
    // Feature flag to use new Performance Rating System
    const useNewPerformanceRating = localStorage.getItem('pptHeroUseNewAnalytics') === 'true';
    
    if (useNewPerformanceRating) {
      console.log('🆕 Profile Scene using new Performance Rating System');
      // Import and use new Performance Rating System
      try {
        const { default: PerformanceRatingSystem } = await import('./ppt-hero/modules/PerformanceRatingSystem.js');
        const performanceSystem = new PerformanceRatingSystem(this);
        await performanceSystem.showPerformanceOverlay();
      } catch (error) {
        console.error('Error loading new Performance Rating System:', error);
        this.showMessage('Error loading analytics. Please try again.', 3000, true);
      }
    } else {
      console.log('📊 Profile Scene using legacy analytics system');
      // Legacy analytics system
      await populateAnalyticsOverlay(auth.currentUser.uid, "PPTHero");
      UIController.showOverlay('analytics-overlay');
    }
  }

  resetPPTData() {
    if (confirm("Are you sure? This cannot be undone.")) {
      const code = prompt("Enter 'RESETMYPPT':");
      if (code === 'RESETMYPPT') {
        handleCalibrationReset(auth.currentUser.uid, code, "PPTHero")
          .then(res => this.showMessage(res.message, 2000, !res.success));
      }
    }
  }

  // Fallback UI for when Rex UI is not available
  createFallbackUI() {
    console.warn("Using fallback UI implementation");
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    
    // Simple text display with basic user info
    const user = auth.currentUser;
    const displayName = this.profileData?.displayName || user?.email || "N/A";
    
    this.add.text(centerX, centerY - 100, "PROFILE SCENE", {
      fontSize: '32px',
      fill: '#00FF00',
      fontFamily: 'monospace',
      fontWeight: 'bold'
    }).setOrigin(0.5);
    
    this.add.text(centerX, centerY - 50, `User: ${displayName}`, {
      fontSize: '18px',
      fill: '#FFFFFF',
      fontFamily: 'monospace'
    }).setOrigin(0.5);
    
    // Create simple back button
    const backButton = MenuUIUtils.createStyledButton(
      this, centerX, centerY + 50, 
      "Back to Menu", 
      () => {
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        sceneManager.switchToScene(useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene");
      },
      { bgColor: 0x666666, width: 200, height: 50 }
    );
  }

  // Resize handler for responsive design
  handleResize() {
    if (!this.scene.isActive()) {
        return;
    }

    // Handle Rex UI Profile resize  
    if (this.rexUIProfile) {
      const centerX = this.cameras.main.width / 2;
      const centerY = this.cameras.main.height / 2;
      
      this.rexUIProfile.setPosition(centerX, centerY);
      this.rexUIProfile.resize(
        Math.min(900, this.cameras.main.width - 40),
        Math.min(700, this.cameras.main.height - 40)
      );
      this.rexUIProfile.layout();
    }

    // Handle TabPages resize
    if (this.profileTabPages) {
      this.profileTabPages.resize(
        Math.min(880, this.cameras.main.width - 60),
        Math.min(580, this.cameras.main.height - 140)
      );
      this.profileTabPages.layout();
    }
  }

  // Clean shutdown with proper manager cleanup
  shutdown() {
    console.log(`[${this.scene.key}] Shutting down...`);
    
    // Clean up Rex UI components
    if (this.rexUIProfile) {
      try {
        this.rexUIProfile.destroy();
      } catch (e) {
        console.warn('Error destroying rexUIProfile:', e);
      }
      this.rexUIProfile = null;
    }
    
    if (this.profileTabPages) {
      try {
        this.profileTabPages.destroy();
      } catch (e) {
        console.warn('Error destroying profileTabPages:', e);
      }
      this.profileTabPages = null;
    }
    
    // Clean up legacy content
    if (this.tabContent && typeof this.tabContent === 'object') {
      Object.values(this.tabContent).forEach(container => {
        if (container && container.destroy) {
          try { container.destroy(true); } catch (e) {}
        }
      });
    }
    this.tabContent = {};

    if (this.uiElements && typeof this.uiElements === 'object') {
      if (this.uiElements.tabsContainer && this.uiElements.tabsContainer.destroy) {
        try { this.uiElements.tabsContainer.destroy(true); } catch (e) {}
      }
    }
    this.uiElements = {};

    super.shutdown();
  }
}
